"use client";

import { useArticleAudio } from "@/context/article-audio-context";
import Icon from "./icon";

type ArticleAudioButtonProps = {
  articleId: string;
  title: string;
  audioUrl: string;
  articleUrl: string;
  imageUrl?: string;
  artworkUrl?: string;
};

const getAbsoluteArtworkUrl = (artworkUrl?: string) => {
  if (!artworkUrl) return undefined;

  const url = new URL(artworkUrl, window.location.origin);
  const sourceUrl = url.searchParams.get("url");
  if (sourceUrl) {
    const source = new URL(sourceUrl);
    source.searchParams.set("fm", "png");
    url.searchParams.set("url", source.href);
  } else {
    url.searchParams.set("fm", "png");
  }
  return url.href;
};

const playInApp = ({
  title,
  audioUrl,
  articleUrl,
  artworkUrl,
}: Pick<
  ArticleAudioButtonProps,
  "title" | "audioUrl" | "articleUrl" | "artworkUrl"
>) => {
  window.postMessage(
    {
      type: "play-article",
      url: audioUrl,
      title,
      show: "EFN",
      imageUrl: getAbsoluteArtworkUrl(artworkUrl),
      articleUrl,
    },
    window.location.origin,
  );
};

/**
 * "Lyssna"-knapp, samma visuella familj som ShareButtons/ShareImageButton
 * (se meta.tsx) - öppnar ArticleAudioPlayer (flytande fält längst ner) via
 * ArticleAudioContext istället för att spela upp inline.
 */
function ArticleAudioButton({
  articleId,
  title,
  audioUrl,
  articleUrl,
  imageUrl,
  artworkUrl,
}: ArticleAudioButtonProps) {
  const { audio, openArticleAudio, closeArticleAudio } = useArticleAudio();
  const isActive = audio?.articleId === articleId;

  const handleClick = () => {
    if (window.efnNativeAudio) {
      playInApp({ title, audioUrl, articleUrl, artworkUrl });
      return;
    }
    if (isActive) {
      closeArticleAudio();
    } else {
      openArticleAudio({ articleId, title, audioUrl, articleUrl, imageUrl });
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        aria-label={isActive ? "Stäng uppläsning" : "Lyssna på artikeln"}
        aria-pressed={isActive}
        className="btn p-0 rounded-sm hover:bg-sand-800 bg-sand-700"
        onClick={handleClick}
      >
        <span className="h-full bg-sand-800 py-2 px-2 rounded-sm md:rounded-none md:rounded-l-sm">
          {isActive ? (
            <Icon.Stop className="size-[18px] fill-black-500 text-black-500" />
          ) : (
            <Icon.Headphones className="size-[18px] text-black-500" />
          )}
        </span>
        <span className="hidden md:block px-2">Lyssna</span>
      </button>
    </div>
  );
}

export default ArticleAudioButton;
